'use client';

import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { ChevronDown, Menu, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { DOC_SECTIONS } from '@/app/[tenant]/mcp/docs/sections';
import { MARKETING_ROUTES, PLATFORM_URL } from '@/common/constants';
import Logo from '@/components/bazak-logo';
import { DemoLink } from '@/components/demo-link';
import { Button } from '@/components/ui/button';
import { INDUSTRIES } from '@/lib/content';
import { cn } from '@/lib/utils';

import { ThemeToggleButton } from './theme-toggle-button';

// Homepage section order. `id` is the matching section on `/` so in-page clicks
// scroll to the component instead of leaving for the dedicated route.
const NAV_LINKS = [
  { label: 'Platform', href: MARKETING_ROUTES.platformHash, id: 'platform' },
  { label: 'How it works', href: MARKETING_ROUTES.howItWorks, id: 'how-it-works' },
  { label: 'Industries', href: MARKETING_ROUTES.industries, id: 'industries' },
  { label: 'Security', href: MARKETING_ROUTES.security, id: 'security' },
  { label: 'News', href: MARKETING_ROUTES.news, id: 'news' },
  { label: 'Pricing', href: MARKETING_ROUTES.pricing, id: 'pricing' },
] as const;

export function Header() {
  const pathname = usePathname();
  const isConnectorDocs = /\/mcp\/docs\/?$/.test(pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
        'a, button, input, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
      wasOpenRef.current = true;
      document.body.style.overflow = 'hidden';
    } else if (wasOpenRef.current) {
      menuButtonRef.current?.focus();
      wasOpenRef.current = false;
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && !industriesOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIndustriesOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, industriesOpen]);

  useEffect(() => {
    if (!industriesOpen) return;
    function handlePointer(e: PointerEvent) {
      if (!industriesRef.current?.contains(e.target as Node)) {
        setIndustriesOpen(false);
      }
    }
    document.addEventListener('pointerdown', handlePointer);
    return () => document.removeEventListener('pointerdown', handlePointer);
  }, [industriesOpen]);

  function closeMenu() {
    setIsOpen(false);
    setIndustriesOpen(false);
  }

  function handleNavClick(e: MouseEvent<HTMLAnchorElement>, elementId: string | null) {
    closeMenu();
    if (pathname !== '/' || !elementId) return;
    e.preventDefault();
    const element = document.getElementById(elementId);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
      history.pushState(null, '', `#${elementId}`);
    }
  }

  return (
    <>
      <header className="fixed top-0 z-50 w-full">
        {/* Backdrop on a sibling layer: WebKit treats `backdrop-filter` as overflow:hidden,
            which would clip the Industries dropdown that hangs below this 60px bar. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 border-b bg-nav backdrop-blur supports-backdrop-filter:bg-nav"
        />
        <div className="relative mx-auto flex h-[60px] max-w-[1200px] items-center px-8">
          <Link href="/" className="me-8 flex items-center" onClick={e => handleNavClick(e, null)}>
            <Logo className="h-7 w-auto" />
          </Link>
          {isConnectorDocs ? (
            <>
              <nav
                aria-label="On this page"
                className="hidden items-center space-x-6 min-[720px]:flex"
              >
                {Object.values(DOC_SECTIONS).map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-sm font-medium underline-offset-4 transition-colors hover:text-primary hover:underline"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
              <div className="flex flex-1 items-center justify-end">
                <ThemeToggleButton />
              </div>
            </>
          ) : (
            <>
              <nav
                aria-label="Main navigation"
                className="hidden flex-1 items-center gap-6 min-[1080px]:flex"
              >
                {NAV_LINKS.map(link =>
                  link.label === 'Industries' ? (
                    <div key={link.label} ref={industriesRef} className="relative">
                      <button
                        type="button"
                        aria-expanded={industriesOpen}
                        aria-haspopup="menu"
                        onClick={() => setIndustriesOpen(open => !open)}
                        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Industries
                        <ChevronDown
                          aria-hidden="true"
                          className={cn(
                            'h-3.5 w-3.5 transition-transform',
                            industriesOpen && 'rotate-180',
                          )}
                        />
                      </button>
                      {industriesOpen && (
                        <div
                          role="menu"
                          className="absolute start-0 top-full z-50 mt-3 min-w-56 rounded-xl border bg-card p-2 shadow-lg"
                        >
                          <Link
                            href={link.href}
                            role="menuitem"
                            onClick={e => handleNavClick(e, link.id)}
                            className="block rounded-lg px-3 py-2 text-sm font-medium hover:bg-muted"
                          >
                            All industries
                          </Link>
                          {INDUSTRIES.map(industry => (
                            <Link
                              key={industry.slug}
                              href={industry.href}
                              role="menuitem"
                              onClick={closeMenu}
                              className="block rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                              {industry.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      key={link.label}
                      href={link.href}
                      onClick={e => handleNavClick(e, link.id)}
                      className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </Link>
                  ),
                )}
              </nav>
              <div className="hidden flex-1 items-center justify-end gap-2 min-[1080px]:flex">
                <Button variant="ghost" size="default" asChild>
                  <a href={PLATFORM_URL}>Log in</a>
                </Button>
                {/* Scan is the filled one, matching the hero. The nav keeps the demo as a bordered
                    button rather than the hero's text link, which would read as another nav item. */}
                <Button size="default" asChild>
                  <Link href={MARKETING_ROUTES.scan}>Scan your brand</Link>
                </Button>
                <Button variant="outline" size="default" asChild>
                  <DemoLink>Get a demo</DemoLink>
                </Button>
                <ThemeToggleButton />
              </div>

              <div className="flex flex-1 items-center justify-end min-[1080px]:hidden">
                <Button
                  ref={menuButtonRef}
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(prev => !prev)}
                  aria-label={isOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={isOpen}
                  aria-controls="mobile-menu"
                  className="relative"
                >
                  <Menu
                    aria-hidden="true"
                    className={`absolute h-5 w-5 transition-all duration-350 ${
                      isOpen ? 'scale-0 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100'
                    }`}
                  />
                  <X
                    aria-hidden="true"
                    className={`absolute h-5 w-5 transition-all duration-350 ${
                      isOpen ? 'scale-100 rotate-0 opacity-100' : 'scale-0 -rotate-90 opacity-0'
                    }`}
                  />
                </Button>
              </div>
            </>
          )}
        </div>
      </header>

      {isOpen && !isConnectorDocs && (
        <div
          className="fixed inset-0 z-40 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 min-[1080px]:hidden"
          style={{ top: '60px' }}
        >
          <nav
            ref={menuRef}
            id="mobile-menu"
            aria-label="Mobile navigation"
            className="flex h-full flex-col px-6 py-6 pb-8"
          >
            <div className="flex-1 space-y-5 overflow-y-auto">
              {NAV_LINKS.map(link =>
                link.label === 'Industries' ? (
                  <div key={link.label} className="space-y-3">
                    <Link
                      href={link.href}
                      onClick={e => handleNavClick(e, link.id)}
                      className="block text-lg font-medium transition-colors hover:text-primary"
                    >
                      Industries
                    </Link>
                    <div className="ms-3 space-y-2 border-s ps-3">
                      {INDUSTRIES.map(industry => (
                        <Link
                          key={industry.slug}
                          href={industry.href}
                          onClick={closeMenu}
                          className="block text-base text-muted-foreground hover:text-foreground"
                        >
                          {industry.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    href={link.href}
                    onClick={e => handleNavClick(e, link.id)}
                    className="block text-lg font-medium transition-colors hover:text-primary"
                  >
                    {link.label}
                  </Link>
                ),
              )}
            </div>
            <div className="space-y-3 border-t pt-6">
              <Button variant="ghost" size="default" className="w-full" asChild>
                <a href={PLATFORM_URL} onClick={closeMenu}>
                  Log in
                </a>
              </Button>
              <Button size="default" className="w-full" asChild>
                <Link href={MARKETING_ROUTES.scan} onClick={closeMenu}>
                  Scan your brand
                </Link>
              </Button>
              <Button variant="outline" size="default" className="w-full" asChild>
                <DemoLink onClick={closeMenu}>Get a demo</DemoLink>
              </Button>
              <div className="flex justify-end">
                <ThemeToggleButton />
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
